import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import momentTimeZone from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';

const ACTION = 'ACTION';
const VIEW = 'VIEW';
const PROFILE = 'PROFILE';

export const trackerTime = () => {
  const estDateTime = momentTimeZone().tz('America/New_York');
  const formattedDateTime = estDateTime.format('YYYY-MM-DD[T]HH:mm:ss');
  return formattedDateTime.toString();
};

const Config = {
  stackSize: 1,
};

const getBrowserName = () => {
  const browserInfo = navigator.userAgent;
  let browser;
  if (browserInfo.includes('Opera') || browserInfo.includes('Opr')) {
    browser = 'Opera';
  } else if (browserInfo.includes('Edg')) {
    browser = 'Edge';
  } else if (browserInfo.includes('Chrome')) {
    browser = 'Chrome';
  } else if (browserInfo.includes('Safari')) {
    browser = 'Safari';
  } else if (browserInfo.includes('Firefox')) {
    browser = 'Firefox';
  } else {
    browser = 'unknown';
  }
  return browser;
};

const getOS = () => {
  const os = navigator.userAgent;
  let finalOs = '';
  if (os.search('Windows') !== -1) {
    finalOs = 'Windows';
  }
  else if (os.search('Mac') !== -1) {
    finalOs = 'MacOS';
  }
  else if (os.search('X11') !== -1 && !(os.search('Linux') !== -1)) {
    finalOs = 'UNIX';
  }
  else if (os.search('Linux') !== -1 && os.search('X11') !== -1) {
    finalOs = 'Linux';
  }
  return finalOs;
};

const defaultContext = {
  clientId: process.env.WEB_CLIENT_ID,
  sessionId: '',
  userId: null,
  deviceId: '',
  trackers: [],
  utm: null,
};

const fetchDeviceId = () => {
  let deviceId = localStorage.getItem('rs_device_id');
  const deviceIdRegistered = localStorage.getItem('rs_device_status');
  if (!deviceId || deviceIdRegistered !== 'registered') {
    deviceId = uuidv4();
    fetch(`${process.env.MS_API_GATEWAY_URL}/api/iam/v1/device`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ deviceId, platform: 'WEB', brand: getOS(), model: getBrowserName() }),
    }).then((resp) => {
      if (resp.status === 200) {
        localStorage.setItem('rs_device_status', 'registered');
      }
    });
    localStorage.setItem('rs_device_id', deviceId);
  }

  return deviceId;
};

export const publishBehaviourMiddleware = async (payLoad) => {
  try {
    const data = await fetch(
      `${process.env.MS_API_GATEWAY_URL}/invoke/audience-manager/v1/audience/tracker/send`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payLoad),
      },
    );
    return Promise.resolve(data.status);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Audience tracker post failed, ', error);
    return Promise.reject(error);
  }
};

const getUtmParams = () => {
  let sessionSource = sessionStorage.getItem('rs_session_source') || null;
  if (sessionSource) {
    sessionSource = JSON.parse(sessionSource);
  }
  const { location: { search } } = window;
  if (search) {
    let queryParams = search.replace('?', '').split('&');
    queryParams = queryParams.filter((qp) => qp.indexOf('utm_') === 0);
    if (queryParams.length) {
      sessionSource = queryParams.filter(q => q).map((qp) => {
        const keyvalue = (qp || '').trim().split('=');
        const param = {};
        if (keyvalue.length === 2) {
          [param.name, param.value] = keyvalue;
        }
        return param;
      }).reduce((p, c) => {
        const { name, value } = c;
        const updated = { ...p, [name.replace('utm_', '')]: value };
        return updated;
      }, {});
      sessionStorage.setItem('rs_session_source', JSON.stringify(sessionSource));
    }
  }
  return sessionSource;
};

const getSessionId = () => {
  let sessionId = sessionStorage.getItem('rs_session_id');
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem('rs_session_id', sessionId);
  }
  return sessionId;
};

export const AudienceTrackerContext = createContext(defaultContext);

const AudienceTrackerProvider = ({ children }) => {
  const [behaviourQueue, setBehaviourQueue] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [behaviour, setBehaviour] = useState(defaultContext);

  const setUserId = useCallback((userId) => {
    setBehaviour(p => ({ ...p, userId }));
  }, []);

  useEffect(() => {
    const deviceId = fetchDeviceId();
    const sessionId = getSessionId();
    const sessionSource = getUtmParams();

    setBehaviour(p => ({ ...p, deviceId, sessionId, utm: sessionSource }));
  }, []);

  const publishBehaviour = useCallback(() => {
    if (behaviour.trackers.length) {
      setPublishing(true);
      publishBehaviourMiddleware(behaviour).then(() => {
        setBehaviour(p => {
          const updatedTrackers = [...behaviourQueue];
          setBehaviourQueue([]);
          return { ...p, trackers: updatedTrackers };
        });
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to publish behaviours.', err);
        setBehaviour(p => {
          const updatedTrackers = [...p.trackers, ...behaviourQueue];
          setBehaviourQueue([]);
          return { ...p, trackers: updatedTrackers };
        });
      }).finally(() => {
        setPublishing(false);
      });
    }
  }, [behaviour]);

  const enrichTracker = useCallback((tracker, type) => {
    return {
      ...tracker,
      trackerType: type,
      timestamp: trackerTime(),
    };
  }, []);

  const addTracker = useCallback((track) => {
    if (publishing) {
      setBehaviourQueue((p) => ([...p, track]));
    } else {
      setBehaviour(p => {
        const updatedTrackers = [...p.trackers, ...behaviourQueue, track];
        setBehaviourQueue([]);
        return { ...p, trackers: updatedTrackers };
      });
    }
  }, [behaviourQueue, publishing]);

  const trackAction = useCallback((tracker, properties) => {
    const actionReady = enrichTracker({ tracker, properties }, ACTION);
    addTracker(actionReady);
  }, [enrichTracker, addTracker]);

  const trackView = useCallback((tracker, properties) => {
    const viewReady = enrichTracker({ tracker, properties }, VIEW);
    addTracker(viewReady);
  }, [enrichTracker, addTracker]);

  const trackProfile = useCallback((tracker, properties) => {
    const profileReady = enrichTracker({ tracker, properties }, PROFILE);
    addTracker(profileReady);
  }, [enrichTracker, addTracker]);

  const trackProfileOnLogin = useCallback((profile, userId) => {
    const deviceId = fetchDeviceId();
    const sessionId = getSessionId();
    const sessionSource = getUtmParams();
    const profileReady = enrichTracker(profile, PROFILE);

    const payLoad = behaviour;
    payLoad.deviceId = deviceId;
    payLoad.sessionId = sessionId;
    payLoad.userId = Number(userId);
    payLoad.utm = sessionSource;
    payLoad.trackers = [profileReady];
    publishBehaviourMiddleware(payLoad);
  }, [enrichTracker]);

  const onBeforeUnload = () => {
    if (behaviour.trackers.length && !publishing) {
      publishBehaviour();
    }
  };

  useEffect(() => {
    try {
      window?.removeEventListener('beforeunload', onBeforeUnload);
      window?.addEventListener('beforeunload', onBeforeUnload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error in AudienceTrackerProvider init', error);
    }
    return () => {
      window?.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [onBeforeUnload]);

  useEffect(() => {
    if (behaviour.trackers.length >= Config.stackSize && !publishing) {
      publishBehaviour();
    }
  }, [behaviour, publishing, publishBehaviour]);

  return (
    <AudienceTrackerContext.Provider value={{ trackAction, trackView, trackProfile, setUserId, trackProfileOnLogin }}>
      {children}
    </AudienceTrackerContext.Provider>
  );

};

AudienceTrackerProvider.propTypes = {
  children: PropTypes.node,
};

export const useAudienceTrackerContext = () => useContext(AudienceTrackerContext);

export default AudienceTrackerProvider;
